import { bookingSearchUrl, viatorSearchUrl, withViatorAffiliate } from './affiliateLinks';
import { primaryCityName } from './knownCityCoordinates';
import { getTripPrimaryLocation, parseTripDate } from './tripUtils';

export const AI_SUGGESTION_RADIUS_MILES = 20;

export const CLIENT_TIERS = ['highend', 'mid', 'budget'];

export const CLIENT_TIER_PRESETS = {
  highend: {
    accommodationStyles: ['luxury', '5-star', 'palace hotel', 'private villa'],
    diningStyle: ['fine dining', 'Michelin', 'destination restaurants'],
    priceRange: '$$$$',
    preferredPace: 'unhurried, highly curated',
    label: 'high-end / luxury'
  },
  mid: {
    accommodationStyles: ['4-star', 'boutique hotel', 'well-reviewed hotels'],
    diningStyle: ['popular local favorites', 'smart casual'],
    priceRange: '$$$',
    preferredPace: 'balanced',
    label: 'mid-tier'
  },
  budget: {
    accommodationStyles: ['value hotels', 'B&B', '3-star'],
    diningStyle: ['casual', 'local trattorias', 'affordable highlights'],
    priceRange: '$–$$',
    preferredPace: 'efficient, good value',
    label: 'budget-conscious'
  }
};

function parseLocations(trip) {
  let locations = trip?.locations;
  if (typeof locations === 'string') {
    try {
      locations = JSON.parse(locations);
    } catch {
      locations = [];
    }
  }
  if (Array.isArray(locations) && locations.length) {
    return locations
      .map((entry) => (typeof entry === 'string' ? entry : entry?.name))
      .map((name) => String(name || '').trim())
      .filter(Boolean);
  }
  if (trip?.first_location) return [trip.first_location];
  if (trip?.location) return [trip.location];
  const primary = getTripPrimaryLocation(trip);
  return primary && primary !== 'No destination set' ? [primary] : [];
}

export function tripLocationNames(trip) {
  return [...new Set(parseLocations(trip).map(primaryCityName).filter(Boolean))];
}

export function tripVicinityInstruction(destinations, miles = AI_SUGGESTION_RADIUS_MILES) {
  if (!destinations?.length) return '';
  return [
    `HARD RADIUS: Stay within ${miles} miles of any of these trip cities: ${destinations.join(', ')}.`,
    `Every hotel, restaurant, place, and activity MUST sit inside that ${miles}-mile radius of at least one listed city.`,
    'Do not use streets, airports, or venues as the center — only the cities.',
    `Do not suggest anything in a different metro, suburb, or country that is farther than ${miles} miles from every trip city.`,
    'If you are unsure a venue is inside the radius, omit it and pick another that is clearly local.'
  ].join(' ');
}

export function formatDestinationCoords(coords = []) {
  return (coords || [])
    .filter((point) => Number.isFinite(Number(point?.lat)) && Number.isFinite(Number(point?.lng)))
    .map((point) => {
      const name = String(point.name || 'city').trim() || 'city';
      return `${name} (${Number(point.lat).toFixed(4)}, ${Number(point.lng).toFixed(4)})`;
    })
    .join('; ');
}

export function tripDayCount(startDate, endDate) {
  const start = parseTripDate(startDate);
  const end = parseTripDate(endDate);
  if (!start || !end) return null;
  const ms = end.getTime() - start.getTime();
  if (!Number.isFinite(ms) || ms < 0) return null;
  return Math.floor(ms / 86400000) + 1;
}

function formatWhen(value) {
  if (!value) return '';
  const text = String(value);
  const datePart = text.slice(0, 10);
  const timePart = text.includes('T') ? text.slice(11, 16) : '';
  return timePart ? `${datePart} ${timePart}` : datePart;
}

function compactAiText(value, max = 120) {
  return String(value || '')
    .replace(/https?:\/\/\S+/gi, '')
    .replace(/\s+/g, ' ')
    .trim()
    .slice(0, max);
}

function formatItineraryLine(item) {
  const visibility = item.client_visible === false || item.client_visible === 0
    ? 'unpublished draft'
    : 'on itinerary';
  const cost = item.cost != null && item.cost !== ''
    ? `${item.cost_currency || 'USD'} ${item.cost}`
    : '';
  const description = compactAiText(item.description, 90);
  const location = compactAiText(item.location, 60);
  const time = formatWhen(item.start_datetime);
  const parts = [
    compactAiText(item.name, 80),
    item.category && `(${item.category})`,
    location && `in ${location}`,
    time && time.includes(' ') && `at ${time.slice(11)}`,
    visibility,
    cost,
    description && `— ${description}`
  ].filter(Boolean);
  return `- ${parts.join(' ')}`;
}

export function formatExistingItinerary(items = []) {
  return (items || []).filter((item) => item?.name).map(formatItineraryLine).join('\n');
}

export function formatDisplayDate(value) {
  const date = parseTripDate(value);
  if (!date) return value ? String(value).slice(0, 10) : '';
  return date.toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' });
}

function clientDisplayName(trip) {
  const named = [trip?.creator_first_name, trip?.creator_last_name].filter(Boolean).join(' ');
  return named || trip?.creator_name || trip?.creator_email || '';
}

export function buildPartnerTripAiBrief({
  trip,
  itinerary = [],
  clientTier = 'highend',
  nearbyCatalog = '',
  destinationCoords = []
} = {}) {
  const destinations = tripLocationNames(trip);
  const preset = CLIENT_TIER_PRESETS[clientTier] || CLIENT_TIER_PRESETS.highend;
  const days = tripDayCount(trip?.start_date, trip?.end_date);
  const travelers = Number(trip?.member_count) > 0 ? Number(trip.member_count) : 1;
  const clientName = clientDisplayName(trip);
  const dateLine = trip?.start_date || trip?.end_date
    ? `${formatDisplayDate(trip?.start_date)} – ${formatDisplayDate(trip?.end_date)}${days ? ` (${days} days)` : ''}`
    : '';
  const coordsLine = formatDestinationCoords(destinationCoords);

  const grouped = new Map();
  (itinerary || []).filter((item) => item?.name).forEach((item) => {
    const key = String(item.start_datetime || '').slice(0, 10) || 'Undated';
    if (!grouped.has(key)) grouped.set(key, []);
    grouped.get(key).push(item);
  });
  const itineraryBlock = grouped.size
    ? Array.from(grouped.entries()).map(([day, items]) => (
      `${formatDisplayDate(day) || day}\n${items.map(formatItineraryLine).join('\n')}`
    )).join('\n\n')
    : '';

  return [
    trip?.title && `Trip: ${trip.title}`,
    destinations.length && `Destinations: ${destinations.join(', ')}`,
    coordsLine && `Trip city centers (lat,lng): ${coordsLine}`,
    dateLine && `Dates: ${dateLine}`,
    trip?.trip_type && `Trip type: ${trip.trip_type}`,
    `Travelers: ${travelers}`,
    clientName && `Client: ${clientName}${trip?.creator_email && trip.creator_email !== clientName ? ` (${trip.creator_email})` : ''}`,
    trip?.budget != null && trip.budget !== '' && `Budget: ${trip.budget_currency || 'USD'} ${trip.budget}`,
    `Client spend level: ${preset.label}. Match hotels, dining, and activities to this level only.`,
    trip?.description && `Desk notes: ${compactAiText(trip.description, 240)}`,
    itineraryBlock
      ? `Already on the itinerary — do not repeat, rename, or near-duplicate any of these stops:\n${itineraryBlock}`
      : 'No stops on the itinerary yet. Propose a strong first set of hotels, dining, and activities that fit the dates, places, and spend level.',
    tripVicinityInstruction(destinations),
    nearbyCatalog
      && `Nearby / popular places already found for this trip — prefer these when they fit the spend level:\n${nearbyCatalog}`,
    'Fill the gaps around what is already planned. Suggest complementary new venues and experiences only.'
  ].filter(Boolean).join('\n\n');
}

export function defaultClientTier(trip) {
  const type = String(trip?.trip_type || '').toLowerCase();
  if (type === 'luxury') return 'highend';
  const budget = Number(trip?.budget);
  if (Number.isFinite(budget) && budget > 0) {
    if (budget >= 8000) return 'highend';
    if (budget <= 2000) return 'budget';
    return 'mid';
  }
  return 'mid';
}

/** Append optional advisor notes / field focus onto the auto-built trip summary. */
export function composePartnerAiBrief({
  tripSummary = '',
  extraContext = '',
  seedPrompt = ''
} = {}) {
  const parts = [String(tripSummary || '').trim()];
  const focus = String(seedPrompt || '').trim();
  const notes = String(extraContext || '').trim();
  if (focus) parts.push(`Field focus:\n${focus}`);
  if (notes) parts.push(`Advisor notes:\n${notes}`);
  return parts.filter(Boolean).join('\n\n');
}

export function buildPartnerTripAiPreferences({
  trip,
  itinerary = [],
  brief = '',
  extraContext = '',
  seedPrompt = '',
  clientTier = 'mid',
  nearbyCatalog = '',
  destinationCoords = []
} = {}) {
  const destinations = tripLocationNames(trip);
  const preset = CLIENT_TIER_PRESETS[clientTier] || CLIENT_TIER_PRESETS.mid;
  const existing = formatExistingItinerary(itinerary);
  const days = tripDayCount(trip?.start_date, trip?.end_date);
  const destinationLabel = destinations.join(', ');
  const coordsLine = formatDestinationCoords(destinationCoords);

  const autoSummary = brief?.trim()
    || [
      trip?.title && `Trip title: ${trip.title}`,
      trip?.description && `Trip description: ${trip.description}`,
      trip?.trip_type && `Trip type: ${trip.trip_type}`,
      days && `Trip length: ${days} days`,
      trip?.budget != null && trip.budget !== ''
        && `Recorded trip budget: ${trip.budget_currency || 'USD'} ${trip.budget}`,
      `Client spend tier: ${preset.label}. Suggest hotels, restaurants, and activities that match this spend level only.`,
      existing
        ? `EXISTING ITINERARY — do not repeat, rename, or near-duplicate any of these stops:\n${existing}`
        : 'The itinerary currently has no stops. Suggest a strong first set that fits the dates, destination, and client tier.',
      tripVicinityInstruction(destinations),
      coordsLine && `Trip city centers (lat,lng): ${coordsLine}`,
      nearbyCatalog
        && `Nearby / popular shortlist:\n${nearbyCatalog}`
    ].filter(Boolean).join('\n\n');

  const additionalContext = composePartnerAiBrief({
    tripSummary: autoSummary,
    extraContext,
    seedPrompt
  });

  return {
    tripName: trip?.title || '',
    destinations,
    destinationCoords: Array.isArray(destinationCoords) ? destinationCoords : [],
    nearbyCatalog,
    toLocation: destinationLabel,
    startDate: trip?.start_date || '',
    endDate: trip?.end_date || '',
    travelers: Number(trip?.member_count) > 0 ? Number(trip.member_count) : 1,
    tripTypes: trip?.trip_type ? [trip.trip_type] : [],
    budget: trip?.budget || '',
    budgetType: 'total',
    accommodationStyles: preset.accommodationStyles,
    diningStyle: preset.diningStyle,
    priceRange: preset.priceRange,
    preferredPace: preset.preferredPace,
    clientTier,
    existingItinerary: existing,
    additionalContext
  };
}

export const AI_PICK_CATEGORIES = ['hotels', 'restaurants', 'locations', 'activities'];

const AI_KIND_KEYS = {
  hotels: 'kindHotel',
  restaurants: 'kindRestaurant',
  locations: 'kindPlace',
  activities: 'kindActivity'
};

const AI_KIND_GROUP_KEYS = {
  hotels: 'kindHotels',
  restaurants: 'kindRestaurants',
  locations: 'kindPlaces',
  activities: 'kindActivities'
};

export function aiPickKindKey(category) {
  return AI_KIND_KEYS[category] || 'kindPlace';
}

export function aiPickGroupKey(category) {
  return AI_KIND_GROUP_KEYS[category] || 'kindPlaces';
}

export function normalizeAiPickName(name) {
  return String(name || '')
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/[^a-z0-9]+/g, ' ')
    .trim();
}

export function namesAreNearDuplicate(left, right) {
  const a = normalizeAiPickName(left);
  const b = normalizeAiPickName(right);
  if (!a || !b) return false;
  if (a === b) return true;
  const [shorter, longer] = a.length <= b.length ? [a, b] : [b, a];
  if (shorter.length < 6) return false;
  return longer.includes(shorter);
}

function pickRichness(item) {
  if (!item) return 0;
  return [
    item.description,
    item.whyRecommended,
    item.address,
    item.website,
    item.googleMapsUrl,
    item.photoName,
    item.rating,
    item.starRating,
    item.priceLevel,
    item.priceRange,
    item.estimatedCost
  ].reduce((sum, value) => sum + String(value || '').length, 0);
}

export function parseAiRating(value) {
  if (value == null || value === '') return null;
  const match = String(value).match(/(\d+(?:\.\d+)?)/);
  if (!match) return null;
  const score = Number(match[1]);
  if (!Number.isFinite(score) || score <= 0) return null;
  if (score > 10) return null;
  return score > 5 ? Number((score / 2).toFixed(1)) : score;
}

export function parseHotelStarClass(value) {
  if (value == null || value === '') return null;
  const match = String(value).match(/(\d)/);
  if (!match) return null;
  const stars = Number(match[1]);
  return stars >= 1 && stars <= 5 ? stars : null;
}

export function aiPickGoogleUrl(item = {}, { name, city } = {}) {
  if (isHttpUrl(item.googleMapsUrl)) return item.googleMapsUrl;
  const query = [item.address || name || item.name, item.city || city]
    .filter(Boolean)
    .join(', ');
  if (!query) return '';
  return `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(query)}`;
}

export function aiPickSummary(item = {}) {
  const text = item.cuisine || item.priceRange || item.estimatedCost || item.description || '';
  return String(text).replace(/\s+/g, ' ').trim().slice(0, 140);
}

export function flattenAiPicks(recommendations) {
  if (!recommendations?.scheduledItinerary) return [];
  const rows = [];

  recommendations.scheduledItinerary.forEach((day, dayIndex) => {
    AI_PICK_CATEGORIES.forEach((category) => {
      (day[category] || []).forEach((item, optionIndex) => {
        if (!item?.name) return;
        const next = {
          key: `${category}-${dayIndex}-${optionIndex}`,
          dayIndex,
          category,
          optionIndex,
          label: item.name,
          day: day.day,
          city: item.city || day.city || '',
          item
        };
        const existingIndex = rows.findIndex(
          (row) => row.category === category && namesAreNearDuplicate(row.label, item.name)
        );
        if (existingIndex === -1) {
          rows.push(next);
          return;
        }
        const existing = rows[existingIndex];
        const richer = pickRichness(item) > pickRichness(existing.item)
          || (pickRichness(item) === pickRichness(existing.item) && item.name.length > existing.label.length);
        if (richer) {
          rows[existingIndex] = { ...next, key: existing.key };
        }
      });
    });
  });

  return rows;
}

export function groupAiPicks(picks = []) {
  return AI_PICK_CATEGORIES
    .map((category) => ({
      category,
      picks: picks.filter((pick) => pick.category === category)
    }))
    .filter((group) => group.picks.length > 0);
}

export function selectedPicksToItinerary(picks = [], selectedOptions = {}) {
  const byDay = new Map();
  picks.forEach((pick) => {
    const chosen = (selectedOptions[pick.category]?.[pick.dayIndex] || []).includes(pick.optionIndex);
    if (!chosen || !pick.item?.name) return;
    const day = Number(pick.day) || 1;
    if (!byDay.has(day)) {
      byDay.set(day, {
        day,
        city: pick.city || '',
        locations: [],
        restaurants: [],
        activities: [],
        hotels: []
      });
    }
    const bucket = byDay.get(day);
    if (AI_PICK_CATEGORIES.includes(pick.category)) {
      bucket[pick.category].push(pick.item);
    }
  });
  return [...byDay.values()].sort((a, b) => a.day - b.day);
}

export function dayNumbersFromCreated(created = [], startDate) {
  if (!startDate) return [];
  const tripStart = new Date(`${startDate}T00:00:00`);
  if (Number.isNaN(tripStart.getTime())) return [];
  const days = new Set();
  created.forEach((item) => {
    const raw = String(item?.start_datetime || '').replace(' ', 'T').slice(0, 19);
    const when = raw ? new Date(raw) : null;
    if (!when || Number.isNaN(when.getTime())) return;
    const start = new Date(tripStart);
    start.setHours(0, 0, 0, 0);
    const dayStart = new Date(when);
    dayStart.setHours(0, 0, 0, 0);
    const number = Math.round((dayStart - start) / 86400000) + 1;
    if (number > 0) days.add(number);
  });
  return [...days].sort((a, b) => a - b);
}

export function isHttpUrl(value) {
  return /^https?:\/\/\S+/i.test(String(value || '').trim());
}

const PLACEHOLDER_URL_RE = /official website url|booking url|reservation url|example\.com|\bn\/a\b|placeholder/i;
const BARE_VENDOR_HOSTS = new Set([
  'viator.com',
  'booking.com',
  'expedia.com',
  'opentable.com',
  'getyourguide.com',
  'tripadvisor.com',
  'google.com'
]);

export function normalizeHttpUrl(value) {
  const raw = String(value || '').trim().split(/\s+/)[0];
  if (!raw || PLACEHOLDER_URL_RE.test(raw)) return '';
  const withProtocol = /^https?:\/\//i.test(raw) ? raw : `https://${raw}`;
  if (!/^https?:\/\/[a-z0-9.-]+\.[a-z]{2,}([/?#].*)?$/i.test(withProtocol)) return '';
  return withProtocol;
}

function hostName(url) {
  const match = String(url || '').match(/^https?:\/\/([^/?#]+)/i);
  return String(match?.[1] || '').replace(/^www\./i, '').toLowerCase();
}

function urlPath(url) {
  const match = String(url || '').match(/^https?:\/\/[^/?#]+(\/[^?#]*)?/i);
  return String(match?.[1] || '').replace(/\/+$/, '');
}

function isBareVendorHome(url) {
  const host = hostName(url);
  return BARE_VENDOR_HOSTS.has(host) && !urlPath(url);
}

function placeQuery(item = {}, { name, city } = {}) {
  return [name || item.name, item.city || city].filter(Boolean).join(' ').trim();
}

export function aiPickWebsiteUrl(item = {}, { name, city } = {}) {
  const official = normalizeHttpUrl(item.website);
  if (official && !isBareVendorHome(official)) return official;
  const query = placeQuery(item, { name, city });
  if (!query) return '';
  return `https://www.google.com/search?q=${encodeURIComponent(`${query} official website`)}`;
}

export function aiPickBookingUrl(item = {}, { name, city, category } = {}) {
  const provided = normalizeHttpUrl(item.bookingUrl);
  if (provided) {
    if (hostName(provided).includes('viator.com')) return withViatorAffiliate(provided);
    if (!isBareVendorHome(provided)) return provided;
  }

  const query = placeQuery(item, { name, city });
  if (!query) return '';
  if (category === 'hotels') return bookingSearchUrl(query);
  if (category === 'activities' || category === 'locations') return viatorSearchUrl(query);
  return `https://www.google.com/search?q=${encodeURIComponent(`${query} reserve table`)}`;
}
